import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbLd } from "@/components/Breadcrumbs";
import Icon from "@/components/Icon";
import { Btn } from "@/components/Btn";
import { Reveal } from "@/components/Reveal";
import { getCity, cityPath, getNeighborsSameCity } from "@/lib/cities";
import { getNeighborhoods, findNeighborhood } from "@/content/citiesContent";
import { TOP_SERVICES, servicePath } from "@/lib/services";
import { BRAND, CTA } from "@/lib/brand";
import NotFound from "@/pages/NotFound";

const introFns = [
  (n, c) => `Need junk gone in ${n.name}? Skedaddle hauls it away fast right here in ${c.name}, where ${n.name} is known for ${n.note}. From a single couch to a full cleanout, we do the heavy lifting so your place is clear again.`,
  (n, c) => `${n.name} is one of our favorite pockets of ${c.name}, with ${n.note}. When the clutter piles up, Skedaddle is a quick call or text away to make it skedaddle, same day when our schedule allows.`,
  (n, c) => `From ${n.note}, ${n.name} keeps its own character in ${c.name}, and we love hauling here. Furniture, appliances, garage and yard cleanouts, if it is in the way in ${n.name}, we will get it gone.`,
];

export default function NeighborhoodPage() {
  const { slug, hood } = useParams();
  const city = getCity(slug);
  const nb = findNeighborhood(slug, hood);
  if (!city || !nb) return <NotFound />;

  const all = getNeighborhoods(slug);
  const idx = all.findIndex((n) => n.slug === hood);
  const intro = introFns[idx % introFns.length](nb, city);
  const others = all.filter((n) => n.slug !== hood).slice(idx, idx + 6);
  const nearbyCities = getNeighborsSameCity(slug, 3);

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Service Areas", to: "/service-areas" },
    { label: `${city.name}, SC`, to: cityPath(city) },
    { label: nb.name },
  ];
  const title = `Junk Removal in ${nb.name}, ${city.name} SC`;
  const desc = `Fast, friendly junk removal in ${nb.name}, ${city.name} SC. Skedaddle hauls furniture, appliances, cleanouts and debris. Get a free quote at 843-518-0021!`;
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service", name: title, serviceType: "Junk Removal",
    areaServed: { "@type": "Place", name: `${nb.name}, ${city.name}, SC` },
    provider: { "@type": "HomeAndConstructionBusiness", name: "Skedaddle Junk Removal", telephone: "+1-843-518-0021" },
    description: desc,
  };

  return (
    <>
      <Seo path={`/service-areas/${slug}/${hood}`} title={title} description={desc} jsonLd={[serviceLd, breadcrumbLd(crumbs)]} noindex="follow" />
      <section className="bg-white border-b-2 border-charcoal py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-8">
          <Breadcrumbs items={crumbs} />
          <div className="inline-flex items-center gap-2 bg-teal text-cream border border-charcoal/20 rounded-full px-4 py-1.5 shadow-brutsm font-body font-bold text-sm mt-6"><MapPin className="w-4 h-4" /> {city.name}, {city.county}</div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tight text-charcoal mt-4">Junk Removal in {nb.name}</h1>
          <p className="mt-4 font-body text-lg md:text-xl text-charcoal/75 max-w-2xl">{intro}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Btn to="/contact" variant="primary" data-testid="hood-quote-btn">{CTA.quote}</Btn>
            <Btn href={BRAND.phoneHref} variant="secondary"><Phone className="w-5 h-5" /> {BRAND.phoneDisplay}</Btn>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 grid md:grid-cols-2 gap-10">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl tracking-tight">What we haul in {nb.name}</h2>
            <div className="mt-5 grid grid-cols-2 gap-2">
              {TOP_SERVICES.slice(0, 8).map((s) => (
                <Link key={s.slug} to={servicePath(s)} className="flex items-center gap-2 font-body font-semibold text-sm hover:text-coral"><span className="w-5 h-5 rounded-full bg-teal text-cream flex items-center justify-center shrink-0"><Check className="w-3 h-3" /></span>{s.title}</Link>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white border border-charcoal/15 rounded-3xl p-7 shadow-brut">
              <h3 className="font-heading font-bold text-2xl">Fast and local</h3>
              <p className="mt-2 font-body text-charcoal/70">We are minutes from {nb.name} and know {city.name} inside out. That means quick arrival windows, honest upfront pricing and a friendly crew that treats your place like our own. One call and it skedaddles!</p>
              <Link to={cityPath(city)} className="mt-4 inline-flex items-center gap-1 font-body font-bold text-coral">All of {city.name} <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-charcoal text-cream py-14 md:py-18">
        <div className="mx-auto max-w-5xl px-4 md:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">More {city.name} areas we serve</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {others.map((n) => (
              <Link key={n.slug} to={`/service-areas/${slug}/${n.slug}`} className="inline-flex items-center gap-2 bg-cream/5 border border-cream/20 rounded-full px-4 py-2 font-body font-semibold text-sm hover:bg-cream/15"><MapPin className="w-4 h-4 text-sunny" /> {n.name}</Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {nearbyCities.map((c) => (
              <Link key={c.slug} to={cityPath(c)} className="font-body font-bold text-sunny underline underline-offset-4">{c.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-coral text-cream py-16 md:py-20 text-center">
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">Junk gone in {nb.name}!</h2>
          <p className="mt-3 font-body text-lg text-cream/90">Free quotes, friendly crew, no pressure.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Btn to="/contact" variant="accent">{CTA.quote}</Btn>
            <Btn href={BRAND.smsHref} variant="white">Text us</Btn>
          </div>
        </div>
      </section>
    </>
  );
}
